
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { isProduction } from '@/integrations/supabase/client'

const Confirm = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')
  const isProd = isProduction()

  useEffect(() => {
    const handleConfirmation = async () => {
      try {
        const type = searchParams.get('type')
        const email = searchParams.get('email')
        const confirmed = searchParams.get('confirmed')

        console.log('Confirmation params:', { type, email, confirmed })

        // For the new webhook flow, we just check if confirmed=true
        if (confirmed === 'true' && email) {
          setStatus('success')
          setMessage('Email confirmed successfully! You can now log in.')
          
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/login?confirmed=true')
          }, 3000)
        } else {
          throw new Error('Invalid confirmation parameters')
        }
      } catch (error: any) {
        console.error('Confirmation error:', error)
        setStatus('error')
        setMessage('Invalid confirmation link. Please try signing up again.')
      }
    }

    handleConfirmation()
  }, [navigate, searchParams])

  // Basic styling for staging/development
  if (!isProd) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow p-6 text-center">
          {status === 'loading' && (
            <>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 mx-auto mb-4"></div>
              <h2 className="text-lg font-medium text-gray-900">Processing confirmation...</h2>
            </>
          )}

          {status === 'success' && (
            <>
              <h2 className="text-lg font-medium text-gray-900 mb-2">Email Confirmed!</h2>
              <p className="text-gray-600 mb-4">{message}</p>
              <Button
                onClick={() => navigate('/login?confirmed=true')}
                className="w-full bg-gray-600 hover:bg-gray-700"
              >
                Continue to Login
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <h2 className="text-lg font-medium text-gray-900 mb-2">Confirmation Failed</h2>
              <p className="text-gray-600 mb-4">{message}</p>
              <div className="space-y-2">
                <Button
                  onClick={() => navigate('/signup')}
                  className="w-full bg-gray-600 hover:bg-gray-700"
                >
                  Back to Sign Up
                </Button>
                <Button
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="w-full"
                >
                  Back to Login
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  // Production styling with purple gradient
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-xl p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900">Processing confirmation...</h2>
            <p className="text-gray-500 mt-2">This may take a few moments</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="text-5xl mb-4">🎉</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Welcome to Dably!</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <Button
              onClick={() => navigate('/login?confirmed=true')}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              Continue to Login
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="text-5xl mb-4">❌</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Confirmation Failed</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-4">
              <Button
                onClick={() => navigate('/signup')}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                Back to Sign Up
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full border-purple-200 text-purple-700 hover:bg-purple-50"
              >
                Back to Login
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Confirm
